import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Lead } from './types';
import { uid, todayStr } from './lib/utils';
import { getApiUrl, setApiUrl, apiGet, apiPost } from './lib/api';
import { TopBar } from './components/TopBar';
import { Tabs } from './components/Tabs';
import { PipelineView } from './components/PipelineView';
import { MetricsView } from './components/MetricsView';
import { FollowView } from './components/FollowView';
import { LeadModal } from './components/LeadModal';
import { SetupScreen } from './components/SetupScreen';
import { SyncState } from './components/SyncBadge';
export function App() {
  const [api, setApi] = useState<string>('');
  const [booted, setBooted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [activeTab, setActiveTab] = useState<'pipe' | 'metrics' | 'follow'>('pipe');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('connecting');
  const [syncText, setSyncText] = useState('Conectando…');
  const apiRef = useRef(api);
  apiRef.current = api;
  const refresh = useCallback(async () => {
    const url = apiRef.current;
    if (!url) return;
    try {
      setSyncState('connecting');
      setSyncText('Sincronizando…');
      const data = await apiGet(url);
      data.sort((a, b) => (b.created || '').localeCompare(a.created || ''));
      setLeads(data);
      setSyncState('ok');
      setSyncText('Sincronizado');
    } catch (e) {
      console.error(e);
      setSyncState('err');
      setSyncText('Sin conexión');
    }
  }, []);
  // Init: check for saved API URL
  useEffect(() => {
    const saved = getApiUrl();
    setLoading(false);
    if (saved) {
      setApi(saved);
      setBooted(true);
    } else {
      setSyncState('err');
      setSyncText('Sin conectar');
    }
  }, []);
  // Boot: when API is set, fetch and start poller
  useEffect(() => {
    if (!booted || !api) return;
    refresh();
    const interval = setInterval(refresh, 60000); // re-sincroniza cada minuto
    return () => clearInterval(interval);
  }, [booted, api, refresh]);
  const handleConnect = (url: string) => {
    setApiUrl(url);
    setApi(url);
    setBooted(true);
  };
  const handleNewLead = () => {
    setEditingLead(null);
    setModalOpen(true);
  };
  const handleLeadClick = (id: string) => {
    const lead = leads.find(l => l.id === id);
    if (lead) {
      setEditingLead(lead);
      setModalOpen(true);
    }
  };
  const handleSaveLead = async (data: Partial<Lead>) => {
    let updated: Lead[];
    let saved: Lead;
    if (data.id) {
      const existing = leads.find(l => l.id === data.id)!;
      saved = {
        ...existing,
        ...data
      } as Lead;
      updated = leads.map(l => l.id === data.id ? saved : l);
    } else {
      saved = {
        id: uid(),
        name: data.name || '',
        phone: data.phone || '',
        zone: data.zone || '',
        type: data.type || 'Tarima flotante',
        m2: data.m2 || '',
        value: data.value || 0,
        src: data.src || 'Meta Ads',
        next: data.next || '',
        notes: data.notes || '',
        stage: data.stage || 'new',
        created: data.created || todayStr()
      };
      updated = [saved, ...leads];
    }
    setLeads(updated);
    setModalOpen(false);
    setEditingLead(null);
    try {
      await apiPost(api, {
        action: 'upsert',
        lead: saved
      });
      setSyncState('ok');
      setSyncText('Guardado');
    } catch (e) {
      setSyncState('err');
      setSyncText('Error al guardar');
      refresh();
    }
  };
  const handleDeleteLead = async (id: string) => {
    setLeads(leads.filter(l => l.id !== id));
    setModalOpen(false);
    setEditingLead(null);
    try {
      await apiPost(api, {
        action: 'delete',
        id
      });
      setSyncState('ok');
      setSyncText('Eliminado');
    } catch (e) {
      setSyncState('err');
      setSyncText('Error');
      refresh();
    }
  };
  if (loading) {
    return <div className="w-full min-h-screen bg-cream" data-id="element-0">
        <div className="max-w-[1240px] mx-auto px-4" data-id="element-1">
          <div className="text-center py-[50px] px-5 text-[#8a8175] font-semibold" data-id="element-2">
            Cargando leads desde Google Sheets…
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-cream" data-id="element-3">
      <div className="max-w-[1240px] mx-auto px-4 pb-[120px]" data-id="element-4">
        <TopBar onNewLead={handleNewLead} syncState={syncState} syncText={syncText} data-id="element-5" />

        {!booted ? <SetupScreen onConnect={handleConnect} data-id="element-6" /> : <>
            <Tabs activeTab={activeTab} onTabChange={setActiveTab} data-id="element-7" />

            {activeTab === 'pipe' && <PipelineView leads={leads} onLeadClick={handleLeadClick} data-id="element-8" />}

            {activeTab === 'metrics' && <MetricsView leads={leads} data-id="element-9" />}

            {activeTab === 'follow' && <FollowView leads={leads} onLeadClick={handleLeadClick} data-id="element-10" />}
          </>}
      </div>

      {booted && <button onClick={handleNewLead} className="fixed bottom-5 right-5 z-50 bg-gradient-to-br from-wood to-wood-dark text-white border-none w-[60px] h-[60px] rounded-2xl shadow-[0_12px_28px_-6px_rgba(193,122,63,0.6)] cursor-pointer flex items-center justify-center hover:scale-105 active:scale-95 transition-all" aria-label="Nuevo lead" data-id="element-11">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-12">
            <line x1="12" y1="5" x2="12" y2="19" data-id="element-13" />
            <line x1="5" y1="12" x2="19" y2="12" data-id="element-14" />
          </svg>
        </button>}

      <LeadModal isOpen={modalOpen} lead={editingLead} onClose={() => {
      setModalOpen(false);
      setEditingLead(null);
    }} onSave={handleSaveLead} onDelete={handleDeleteLead} data-id="element-15" />
    </div>;
}